// The milestone cinematic director (pacing spec §6 C2). Framework-free so
// the rules are unit-testable; useCinematicDirector wires it into React.
//
//   offer(milestone, tick)  decide: ignore / filter by setting / drop a
//                           replay / play now / queue / (multiplayer) toast
//   update(tick)            new game time: start the queued montage once the
//                           90 s game-time cooldown has passed
//   frame()                 once per animation frame: move the camera, fire
//                           sound cues, finish when the timeline ends
//   skip()                  end now: restore the camera pose, resume the sim
//                           only if the cinematic paused it
//
// Rules:
//   - only the local player's milestones play, plus anyone's victory;
//   - `cinematics` setting: all / big (big + finale) / off;
//   - one cinematic per RATE_LIMIT_TICKS of game time (the finale always
//     plays); whatever arrives meanwhile is merged into one montage;
//   - never the same milestone twice for the same entity;
//   - single-player pauses the sim through the pause controller's
//     'cinematic' claim; multiplayer never pauses and shows a toast instead.

import type { MilestonePayload } from '@aod/protocol';
import type { CinematicsSetting } from '../settings/useSettings.js';
import type { CinematicCamera } from './cameraBridge.js';
import { RECIPES, payloadFocus, recipeIdFor } from './recipes.js';
import { type Timeline, buildTimeline, sampleCamera, visualStateAt } from './timeline.js';
import type { CameraPose, RecipeId, UiMode, Vec3 } from './types.js';

/** 90 s of game time at the base 10 ticks/s (sim/gameTime.ts: 10 ticks = 1 day ≈ 1 s). */
export const RATE_LIMIT_TICKS = 900;
/** Multiplayer toasts stay this long. */
export const TOAST_MS = 5000;

export type { CinematicsSetting };
export type DirectorMode = 'single' | 'multiplayer';

export type OfferResult =
  | 'ignored' // another player's milestone
  | 'filtered' // the cinematics setting doesn't show it
  | 'duplicate' // already shown for this entity
  | 'played'
  | 'queued' // cooldown or another cinematic running: joins the montage
  | 'toast'; // multiplayer

/** Milestones that can legitimately repeat, so their tick is part of the identity. */
const PER_EVENT = new Set<string>([
  'city.capital_captured',
  'magic.ritual_completed',
  'diplomacy.peace_signed',
  'battle.decisive',
  'wonder.built',
  // A Champion can be summoned and fall more than once per match.
  'legend.summoned',
  'legend.fallen',
]);

/**
 * Champion moments are rare, player-triggered and time-sensitive: they skip the
 * global rate limit (like the finale) but still queue behind a cinematic that is
 * on screen right now.
 */
export function bypassesRateLimit(p: MilestonePayload): boolean {
  return p.milestoneId === 'legend.summoned' || p.milestoneId === 'legend.fallen';
}

export function isForLocalPlayer(p: MilestonePayload, localPlayer: number): boolean {
  return p.playerIndex === localPlayer || p.milestoneId === 'victory.achieved';
}

export function passesSetting(p: MilestonePayload, setting: CinematicsSetting): boolean {
  if (setting === 'off') return false;
  if (setting === 'all') return true;
  return p.tier === 'big' || p.tier === 'finale';
}

/**
 * Identity for "never replay": milestone + entity (or player) + the value
 * that makes a repeat a new moment (a city's new tier, a hero's level…).
 */
export function milestoneKey(p: MilestonePayload): string {
  const who = p.entityId !== undefined ? `e${p.entityId}` : `p${p.playerIndex}`;
  const d = p.data ?? {};
  // `name` participates: two graduation moments (or any named no-entity
  // moment) for the same player must not dedup to the same key.
  const detail = [d.tier, d.level, d.era, d.archetype, d.ritual, d.otherPlayerIndex, d.path, d.name]
    .filter((v) => v !== undefined)
    .join(',');
  const event = PER_EVENT.has(p.milestoneId) && p.entityId === undefined ? `@${p.tick}` : '';
  return `${p.milestoneId}|${who}|${detail}${event}`;
}

export interface DirectorConfig {
  readonly mode: DirectorMode;
  readonly setting: CinematicsSetting;
  readonly localPlayer: number;
  readonly uiMode: UiMode;
}

/** How the director pauses the sim (the pause controller's 'cinematic' claim). */
export interface PauseHandle {
  /**
   * Ensure the game is paused for the cinematic. True when the cinematic is
   * responsible for resuming it: this call paused the game, or the pause was
   * already the cinematic's. False when the game is paused for someone else
   * (the player, an away/danger card) — the cinematic must not resume it.
   */
  pause(): boolean;
  /** Resume, but only if our pause still holds. */
  resume(): void;
}

export interface WorldFraming {
  readonly capital: Vec3 | null;
  readonly empire: { readonly center: Vec3; readonly radius: number } | null;
}

export interface DirectorDeps {
  readonly getCamera: () => CinematicCamera | null;
  readonly getPause: () => PauseHandle | null;
  readonly getWorld: () => WorldFraming;
  readonly prefersReducedMotion: () => boolean;
  readonly playSound?: (soundId: string) => void;
  /** Wall clock in ms (performance.now in the app). */
  readonly now: () => number;
}

export interface ActiveCinematic {
  readonly id: number;
  readonly recipeId: RecipeId;
  /** One milestone, or several for a montage. */
  readonly milestones: ReadonlyArray<MilestonePayload>;
  readonly timeline: Timeline;
  readonly startedAtMs: number;
  readonly uiMode: UiMode;
  readonly pausedByUs: boolean;
  readonly savedPose: CameraPose | null;
}

export interface MilestoneToast {
  readonly id: number;
  readonly milestone: MilestonePayload;
  readonly uiMode: UiMode;
}

export interface DirectorState {
  readonly active: ActiveCinematic | null;
  readonly toasts: ReadonlyArray<MilestoneToast>;
}

export interface OfferOptions {
  /** Kid layer override (e.g. from the test bridge). */
  readonly uiMode?: UiMode;
  /**
   * Test bridge only: play immediately, cutting short whatever is on screen
   * and ignoring the cooldown — same path a finale takes. Without it, an
   * injected milestone legitimately queues behind a natural one.
   */
  readonly force?: boolean;
}

const DEFAULT_CONFIG: DirectorConfig = {
  mode: 'single',
  setting: 'big',
  localPlayer: 0,
  uiMode: 'standard',
};

export class CinematicDirector {
  private config: DirectorConfig = DEFAULT_CONFIG;
  private state: DirectorState = { active: null, toasts: [] };
  private readonly listeners = new Set<() => void>();
  private readonly seen = new Set<string>();
  private pending: MilestonePayload[] = [];
  private pendingUiMode: UiMode | null = null;
  private lastStartTick: number | null = null;
  private lastTick = 0;
  private nextId = 1;
  private firedCues = 0;
  /** Where earlier big moments happened, for the finale's quick cuts. */
  private readonly history: Vec3[] = [];

  constructor(private readonly deps: DirectorDeps) {}

  // ── store plumbing (useSyncExternalStore) ──
  readonly subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  readonly getState = (): DirectorState => this.state;
  private set(patch: Partial<DirectorState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  configure(patch: Partial<DirectorConfig>): void {
    this.config = { ...this.config, ...patch };
  }

  getConfig(): DirectorConfig {
    return this.config;
  }

  /** Milestones waiting for the cooldown (merged into one montage). */
  getPending(): ReadonlyArray<MilestonePayload> {
    return this.pending;
  }

  offer(p: MilestonePayload, tick: number, opts: OfferOptions = {}): OfferResult {
    const { localPlayer, setting, mode } = this.config;
    this.lastTick = Math.max(this.lastTick, tick);
    if (!isForLocalPlayer(p, localPlayer)) return 'ignored';
    if (!passesSetting(p, setting)) return 'filtered';
    const key = milestoneKey(p);
    if (this.seen.has(key)) return 'duplicate';
    this.seen.add(key);
    const uiMode = opts.uiMode ?? this.config.uiMode;

    if (mode === 'multiplayer') {
      const id = this.nextId++;
      this.set({ toasts: [...this.state.toasts, { id, milestone: p, uiMode }] });
      return 'toast';
    }

    if (p.tier === 'finale' || p.milestoneId === 'victory.achieved' || opts.force) {
      // The finale always plays, cutting short whatever is on screen. The
      // test bridge's force option takes the same path so audits don't queue
      // behind a natural milestone's cooldown.
      this.pending = [];
      this.start([p], tick, uiMode);
      return 'played';
    }

    if (this.state.active === null && (bypassesRateLimit(p) || this.cooledDown(tick))) {
      this.start([p], tick, uiMode);
      return 'played';
    }
    this.pending.push(p);
    if (opts.uiMode) this.pendingUiMode = opts.uiMode;
    return 'queued';
  }

  update(tick: number): void {
    if (tick < this.lastTick) this.reset(); // new match or loaded save
    this.lastTick = tick;
    if (this.state.active || this.pending.length === 0) return;
    if (!this.cooledDown(tick) && !this.pending.some(bypassesRateLimit)) return;
    const batch = this.pending;
    const uiMode = this.pendingUiMode ?? this.config.uiMode;
    this.pending = [];
    this.pendingUiMode = null;
    this.start(batch, tick, uiMode);
  }

  frame(): void {
    const active = this.state.active;
    if (!active) return;
    const t = this.deps.now() - active.startedAtMs;
    this.fireCues(active, t);
    const camera = this.deps.getCamera();
    if (camera) {
      const pose = sampleCamera(active.timeline, t);
      if (pose) camera.setPose(pose);
      camera.setGolden(visualStateAt(active.timeline, t).golden);
    }
    if (t >= active.timeline.durationMs) this.finish();
  }

  /** Time into the running cinematic, on the director's clock (0 when idle). */
  elapsedMs(): number {
    const active = this.state.active;
    return active ? this.deps.now() - active.startedAtMs : 0;
  }

  skip(): void {
    if (this.state.active) this.finish();
  }

  dismissToast(id: number): void {
    this.set({ toasts: this.state.toasts.filter((t) => t.id !== id) });
  }

  /** Forget everything (new match). Ends a running cinematic cleanly. */
  reset(): void {
    if (this.state.active) this.finish();
    this.seen.clear();
    this.pending = [];
    this.pendingUiMode = null;
    this.lastStartTick = null;
    this.lastTick = 0;
    this.history.length = 0;
    if (this.state.toasts.length) this.set({ toasts: [] });
  }

  // ── internals ──

  private cooledDown(tick: number): boolean {
    return this.lastStartTick === null || tick - this.lastStartTick >= RATE_LIMIT_TICKS;
  }

  private start(milestones: MilestonePayload[], tick: number, uiMode: UiMode) {
    const prev = this.state.active;
    const camera = this.deps.getCamera();
    // A finale that cuts in inherits the player's original camera pose: the
    // camera must end where the player left it, not where the moment it
    // interrupted was.
    const savedPose = prev ? prev.savedPose : (camera?.getPose() ?? null);
    // The pause is taken fresh, never inherited. The cinematic on screen may
    // have lost its claim (the player resumed by hand), and inheriting its
    // stale "we paused it" flag would leave the game simulating under this
    // one — and resume a pause the player owns when it ends.
    const pausedByUs = this.acquirePause();
    if (prev) this.firedCues = 0;

    const first = milestones[0] as MilestonePayload;
    const recipeId: RecipeId =
      milestones.length > 1 ? 'montage' : recipeIdFor(first, this.config.localPlayer);
    const world = this.deps.getWorld();
    const focus =
      milestones.map(payloadFocus).find((v): v is Vec3 => v !== null) ?? world.capital ?? null;
    const startPose = camera?.getPose() ??
      savedPose ?? { position: [0, 10, 10], target: [0, 0, 0] };
    const timeline = buildTimeline(RECIPES[recipeId], {
      startPose,
      focus,
      capital: world.capital,
      empire: world.empire,
      highlights: [...this.history],
      reducedMotion: this.deps.prefersReducedMotion(),
    });
    for (const m of milestones) {
      const pos = payloadFocus(m);
      if (pos && m.tier === 'big') this.history.push(pos);
    }
    if (this.history.length > 3) this.history.splice(0, this.history.length - 3);

    camera?.setActive(true);
    this.lastStartTick = tick;
    this.firedCues = 0;
    this.set({
      active: {
        id: this.nextId++,
        recipeId,
        milestones,
        timeline,
        startedAtMs: this.deps.now(),
        uiMode,
        pausedByUs,
        savedPose,
      },
    });
    this.fireCues(this.state.active as ActiveCinematic, 0);
  }

  private acquirePause(): boolean {
    if (this.config.mode !== 'single') return false;
    return this.deps.getPause()?.pause() ?? false;
  }

  private fireCues(active: ActiveCinematic, t: number) {
    const cues = active.timeline.cues;
    while (this.firedCues < cues.length && (cues[this.firedCues] as { atMs: number }).atMs <= t) {
      const cue = cues[this.firedCues++];
      if (cue?.kind === 'sting') this.deps.playSound?.(cue.soundId);
    }
  }

  private finish() {
    const active = this.state.active;
    if (!active) return;
    const camera = this.deps.getCamera();
    if (camera) {
      camera.setGolden(0);
      if (active.savedPose) camera.setPose(active.savedPose);
      camera.setActive(false);
    }
    if (active.pausedByUs) this.deps.getPause()?.resume();
    this.set({ active: null });
  }
}
